"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { HARD_SIGN_OUT_EVENT } from "@/lib/auth/clientSignOut";
import {
  clearSabineChatSession,
  loadSabineChatMessages,
  saveSabineChatMessages,
} from "@/server-actions/sabine-chat/actions";

const SAVE_DEBOUNCE_MS = 400;
const LOAD_FAILURE_RETRY_COOLDOWN_MS = 5000;
const SABINE_CHAT_BEACON_URL = "/api/sabine-chat/save";

const inFlightLoadMap = new Map();
const recentLoadFailureMap = new Map();
const messageCache = new Map();

function getPersistenceSessionKey(source, contextId) {
  return `${source}::${contextId ?? "__global__"}`;
}

function cloneMessages(messages) {
  return Array.isArray(messages) ? messages.map((message) => ({ ...message })) : [];
}

function isInitialOnlyMessages(messages, initialMessage) {
  return (
    messages?.length === 1 &&
    initialMessage &&
    messages[0]?.id === initialMessage.id
  );
}

function flushSaveViaBeacon({ source, contextId, surface, messages }) {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.sendBeacon !== "function"
  ) {
    return false;
  }

  try {
    const payload = JSON.stringify({ source, contextId, surface, messages });
    const blob = new Blob([payload], { type: "application/json" });
    return navigator.sendBeacon(SABINE_CHAT_BEACON_URL, blob);
  } catch (error) {
    console.error("[useSabineChatPersistence] sendBeacon failed", error);
    return false;
  }
}

/**
 * Keeps one Sabine thread in its saved row: `source` and `contextId` key the
 * row, `surface` is the Sabine surface it is saved from
 * (lib/sabine/surfaces.js), which her recall tool reads it back by.
 */
export function useSabineChatPersistence({
  source = "GENERIC",
  contextId = null,
  surface = null,
  initialMessage,
  messages,
  setMessages,
  enabled = true,
}) {
  const [isLoaded, setIsLoaded] = useState(!enabled);
  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const hardSignOutRef = useRef(false);

  // Latest values mirrored to refs so the unmount/pagehide flush can read
  // them without stale closures or depending on effect-update timing.
  const setMessagesRef = useRef(setMessages);
  setMessagesRef.current = setMessages;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const contextIdRef = useRef(contextId);
  contextIdRef.current = contextId;
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;
  const initialMessageRef = useRef(initialMessage);
  initialMessageRef.current = initialMessage;
  const isLoadedRef = useRef(false);
  isLoadedRef.current = isLoaded;
  // undefined = first run; null = persistence disabled.
  const activeSessionKeyRef = useRef(undefined);

  useEffect(() => {
    const sessionKey = enabled
      ? getPersistenceSessionKey(source, contextId)
      : null;
    const previousSessionKey = activeSessionKeyRef.current;
    activeSessionKeyRef.current = sessionKey;

    // Persistence turned off or pointed at a different context: the prior
    // conversation must not remain visible, nor be saved under the new key.
    if (previousSessionKey !== undefined && previousSessionKey !== sessionKey) {
      setMessagesRef.current(
        initialMessageRef.current ? [initialMessageRef.current] : [],
      );
    }

    if (!enabled) {
      if (previousSessionKey !== undefined && saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      setIsLoaded(true);
      lastSavedRef.current = null;
      return undefined;
    }

    setIsLoaded(false);
    lastSavedRef.current = null;
    let cancelled = false;

    async function load() {
      const cachedMessages = messageCache.get(sessionKey);
      if (!cancelled && cachedMessages?.length) {
        setMessagesRef.current(cloneMessages(cachedMessages));
      }

      const lastFailureAt = recentLoadFailureMap.get(sessionKey);
      if (
        typeof lastFailureAt === "number" &&
        Date.now() - lastFailureAt < LOAD_FAILURE_RETRY_COOLDOWN_MS
      ) {
        if (!cancelled) {
          setIsLoaded(true);
        }
        return;
      }

      try {
        let loadPromise = inFlightLoadMap.get(sessionKey);

        if (!loadPromise) {
          loadPromise = loadSabineChatMessages({ source, contextId })
            .then((saved) => {
              recentLoadFailureMap.delete(sessionKey);
              messageCache.set(sessionKey, cloneMessages(saved));
              return saved;
            })
            .catch((error) => {
              recentLoadFailureMap.set(sessionKey, Date.now());
              throw error;
            })
            .finally(() => {
              inFlightLoadMap.delete(sessionKey);
            });

          inFlightLoadMap.set(sessionKey, loadPromise);
        }

        const saved = await loadPromise;
        if (cancelled) return;

        if (saved && Array.isArray(saved) && saved.length > 0) {
          setMessagesRef.current(cloneMessages(saved));
        }
      } catch (error) {
        console.error("[useSabineChatPersistence] Failed to load messages", error);
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [source, contextId, enabled]);

  // Persist the latest messages right now, bypassing the debounce. Used by
  // the unmount cleanup and by tab-hide / page-unload listeners so a pending
  // assistant response is never lost just because the user clicks away.
  const flushPendingSave = useCallback(({ useBeacon = false } = {}) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (!enabled || !isLoadedRef.current) return;

    const currentMessages = messagesRef.current;
    const currentSource = sourceRef.current;
    const currentContextId = contextIdRef.current;
    const currentSurface = surfaceRef.current;
    const currentInitial = initialMessageRef.current;

    if (
      !currentMessages?.length ||
      isInitialOnlyMessages(currentMessages, currentInitial)
    ) {
      return;
    }

    const signature = JSON.stringify(currentMessages);
    if (lastSavedRef.current === signature) return;

    const sessionKey = getPersistenceSessionKey(currentSource, currentContextId);

    if (useBeacon) {
      const ok = flushSaveViaBeacon({
        source: currentSource,
        contextId: currentContextId,
        surface: currentSurface,
        messages: currentMessages,
      });
      if (ok) {
        lastSavedRef.current = signature;
        messageCache.set(sessionKey, cloneMessages(currentMessages));
      }
      return;
    }

    if (hardSignOutRef.current) return;

    // Fire-and-forget: the server action's underlying fetch keeps running
    // even after the React tree unmounts on SPA navigation.
    saveSabineChatMessages({
      source: currentSource,
      contextId: currentContextId,
      surface: currentSurface,
      messages: currentMessages,
    })
      .then(() => {
        messageCache.set(sessionKey, cloneMessages(currentMessages));
        lastSavedRef.current = signature;
      })
      .catch((error) => {
        if (!hardSignOutRef.current) {
          console.error("[useSabineChatPersistence] Failed to flush save", error);
        }
      });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!isLoaded) return;

    if (
      !messages?.length ||
      isInitialOnlyMessages(messages, initialMessage)
    ) {
      return;
    }

    const signature = JSON.stringify(messages);
    if (lastSavedRef.current === signature) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const messagesSnapshot = messages;

    saveTimeoutRef.current = setTimeout(async () => {
      saveTimeoutRef.current = null;
      try {
        await saveSabineChatMessages({
          source,
          contextId,
          surface,
          messages: messagesSnapshot,
        });
        messageCache.set(
          getPersistenceSessionKey(source, contextId),
          cloneMessages(messagesSnapshot),
        );
        lastSavedRef.current = signature;
      } catch (error) {
        if (!hardSignOutRef.current) {
          console.error("[useSabineChatPersistence] Failed to save messages", error);
        }
      }
    }, SAVE_DEBOUNCE_MS);
    // No cleanup that clears the timeout here: that was the original bug.
    // If messages change again, the next effect run will clearTimeout above.
    // If the component unmounts, the dedicated unmount-flush effect below
    // will clear the timeout and fire the save synchronously.
  }, [messages, isLoaded, source, contextId, surface, initialMessage, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;

    const handlePageHide = () => flushPendingSave({ useBeacon: true });
    const handleHardSignOut = () => {
      hardSignOutRef.current = true;
      flushPendingSave({ useBeacon: true });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSave({ useBeacon: true });
      }
    };

    window.addEventListener(HARD_SIGN_OUT_EVENT, handleHardSignOut);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener(HARD_SIGN_OUT_EVENT, handleHardSignOut);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Component unmount (typically SPA navigation away from the page).
      // Flush via the regular server action so the request continues
      // running independently of the React lifecycle.
      flushPendingSave({ useBeacon: false });
    };
  }, [flushPendingSave, enabled]);

  const resetAndClear = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    setMessagesRef.current(initialMessage ? [initialMessage] : []);
    lastSavedRef.current = null;
    const sessionKey = getPersistenceSessionKey(source, contextId);
    messageCache.delete(sessionKey);
    recentLoadFailureMap.delete(sessionKey);
    inFlightLoadMap.delete(sessionKey);

    if (!enabled) {
      return;
    }

    try {
      await clearSabineChatSession({ source, contextId });
    } catch (error) {
      console.error("[useSabineChatPersistence] Failed to clear session", error);
    }
  }, [source, contextId, initialMessage, enabled]);

  return { isLoaded, resetAndClear };
}
